/**
 * <SettingsEntryCard /> — the per-entry card used on the Settings
 * hub landing (M3X.UIPOLISH PR 1).
 *
 * Per spec §1.A.1: every Settings entry is a card with:
 *   - Brand-colored icon top-left
 *   - Title (bilingual via t())
 *   - 1-sentence description
 *   - Optional metadata row (count, last modified, status)
 *   - Click → navigates to the configuration page
 *
 * Design rules (per DESIGN_SYSTEM.md §3-5):
 *   - 2px brand-gradient strip on top (the Solvait signature)
 *   - Hover state shows "Configure →" with arrow
 *   - Card content stacks naturally — bilingual text + RTL safe
 *   - axe-clean: each card is a proper Link with aria-label
 */

import type { LucideIcon } from "lucide-react";
import { ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

import type { KPITone } from "./KPICard";

export interface SettingsEntryCardProps {
  /** Lucide icon for the card. */
  icon: LucideIcon;
  /** Localized title (already translated). */
  title: string;
  /** Localized description (already translated). */
  description: string;
  /** Where the card navigates on click. */
  to: string;
  /** Optional small status text under the description (count,
   * "Last modified X", etc.). */
  meta?: string;
  /** Tonal color — drives the icon background. Default 'indigo'
   * (brand light blue retint). */
  tone?: KPITone;
  /** If true, renders a "Coming soon" muted ghost style instead of
   * a link. */
  comingSoon?: boolean;
}

const ICON_TONE_CLASS: Record<KPITone, string> = {
  amber: "bg-amber-100 text-amber-700",
  indigo: "bg-ai-bg text-ai-deep",
  teal: "bg-brand-teal/15 text-brand-teal",
  rose: "bg-rose-100 text-rose-700",
  green: "bg-emerald-100 text-emerald-700",
  purple: "bg-purple-100 text-purple-700",
  neutral: "bg-surface-muted text-text-secondary",
};

/**
 * Per spec — every card has a 2px brand-gradient strip on top.
 * Re-uses the same brand-gradient utility used by KPICard /
 * top-bar accent strip.
 */
function GradientStrip() {
  return (
    <div
      aria-hidden
      className="absolute inset-x-0 top-0 h-[2px] bg-brand-gradient-h opacity-80"
    />
  );
}

export function SettingsEntryCard({
  icon: Icon,
  title,
  description,
  to,
  meta,
  tone = "indigo",
  comingSoon = false,
}: SettingsEntryCardProps) {
  const { t } = useTranslation();
  const toneClass = ICON_TONE_CLASS[tone];

  const inner = (
    <>
      <GradientStrip />
      <div className="flex items-start gap-3">
        <div
          className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${toneClass}`}
        >
          <Icon size={18} aria-hidden />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-semibold text-text-primary leading-tight">
            {title}
          </h3>
          <p className="mt-1 text-xs text-text-secondary leading-snug">
            {description}
          </p>
          {meta && (
            <p className="mt-1.5 text-[11px] text-text-tertiary">{meta}</p>
          )}
        </div>
      </div>
      {!comingSoon && (
        <div className="mt-3 flex items-center justify-end text-[11px] font-medium text-ai-deep opacity-0 transition-opacity group-hover:opacity-100">
          <span>{t("settings_hub.configure_cta")}</span>
          <ArrowRight
            size={12}
            className="ms-1 rtl:rotate-180"
            aria-hidden
          />
        </div>
      )}
      {comingSoon && (
        <div className="mt-3 text-[11px] text-text-tertiary">
          {t("settings_hub.coming_soon")}
        </div>
      )}
    </>
  );

  if (comingSoon) {
    return (
      <div
        className="group relative flex flex-col rounded-xl border border-border-subtle bg-surface-default p-4 opacity-60"
        aria-label={`${title} — ${t("settings_hub.coming_soon")}`}
      >
        {inner}
      </div>
    );
  }

  return (
    <Link
      to={to}
      className="group relative flex flex-col rounded-xl border border-border-subtle bg-surface-default p-4 transition-shadow hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-ai/40"
      aria-label={`${title} — ${description}`}
    >
      {inner}
    </Link>
  );
}

export default SettingsEntryCard;
